import { HttpService } from "../modules/shared/services/http.service";
import { PageManagement } from "../modules/shared/model/page-management.interfaces";

export class PageService {
  /**
   * Fetch a page by its URL
   * @param url - The page URL (e.g., "/about-us", "/contact")
   * @returns Promise<PageManagement | null>
   */
  static async getPageByUrl(url: string): Promise<PageManagement | null> {
    try {
      // Ensure URL starts with /
      const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
      
      // Get all pages and filter by URL
      const response = await HttpService.get(`api/v1/page-managements?filters=%7B%22url%22%3A%22${normalizedUrl}%22%7D`, true, { 
        "x-custom-lang": "en" 
      });
      
      if (response && response.data && Array.isArray(response.data)) {
        const page = response.data.find((p: any) => p.url.includes(normalizedUrl));
        
        if (page) {
          // Convert API response to PageManagement format
          return {
            id: page.id,
            status: page.status,
            type: page.type,
            enableRoleBased: page.enableRoleBased,
            isText: page.isText,
            metaKeywords: page.metaKeywords,
            metaDescription: page.metaDescription,
            content: page.content,
            url: page.url,
            title: page.title,
            createdAt: page.createdAt,
            updatedAt: page.updatedAt
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching page by URL:", error);
      return null;
    }
  }

  /**
   * Get all published pages
   * @returns Promise<PageManagement[]>
   */
  static async getPublishedPages(): Promise<PageManagement[]> {
    try {
      const response = await HttpService.get("api/v1/page-managements", true, { 
        "x-custom-lang": "en" 
      });
      
      if (response && response.data && Array.isArray(response.data)) {
        console.log("response.data", response);
        return response.data
          .filter((page: any) => Number(page.status?.id) === 2) // 2 = Published
          .map((page: any) => ({
            id: page.id,
            status: page.status,
            type: page.type,
            enableRoleBased: page.enableRoleBased,
            isText: page.isText,
            metaKeywords: page.metaKeywords,
            metaDescription: page.metaDescription,
            content: page.content,
            url: page.url,
            title: page.title,
            createdAt: page.createdAt,
            updatedAt: page.updatedAt
          }));
      }
      
      return [];
    } catch (error) {
      console.error("Error fetching published pages:", error);
      return [];
    }
  }

  /**
   * Check if a page exists and is published
   * @param url - The page URL
   * @returns Promise<boolean>
   */
  static async isPagePublished(url: string): Promise<boolean> {
    try {
      const page = await this.getPageByUrl(url);
      return page !== null && Number(page.status?.id) === 2; // 2 = Published
    } catch (error) {
      console.error("Error checking page status:", error);
      return false;
    }
  }
}
